import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';

export interface AnalysisHistoryItem {
  revisionId: string;
  fileName: string | null;
  createdAt: string;
  completedAt: string | null;
  status: string;
  flagCount: number;
  highRiskFlags: number;
  riskScore: number;
  criticalCount: number;
  highCount: number;
  mediumCount: number;
  lowCount: number;
}

export function useAnalysisHistory(auditId: string, moduleType: string) {
  const [history, setHistory] = useState<AnalysisHistoryItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    async function fetchHistory() {
      try {
        setIsLoading(true);
        setError(null);

        // Hämta revisions för denna audit och modul
        const { data: revisions, error: revisionsError } = await supabase
          .from('revisions')
          .select('*')
          .eq('audit_id', auditId)
          .eq('module_type', moduleType)
          .eq('status', 'completed')
          .order('created_at', { ascending: false })
          .limit(5);

        if (revisionsError) throw revisionsError;

        if (!revisions || revisions.length === 0) {
          setHistory([]);
          setIsLoading(false);
          return;
        }

        // Hämta aggregates för varje revision
        const revisionIds = revisions.map(r => r.id);
        const { data: aggregates, error: aggregatesError } = await supabase
          .from('aggregates')
          .select('*')
          .in('revision_id', revisionIds);

        if (aggregatesError) throw aggregatesError;

        // Hämta batches för att räkna severity-fördelning
        const { data: batches, error: batchesError } = await supabase
          .from('batches')
          .select('revision_id, result_json')
          .in('revision_id', revisionIds)
          .eq('state', 'completed');

        if (batchesError) throw batchesError;

        // Bygg historiklista
        const historyItems: AnalysisHistoryItem[] = revisions.map(revision => {
          const aggregate = aggregates?.find(a => a.revision_id === revision.id);
          const revisionBatches = batches?.filter(b => b.revision_id === revision.id) || [];

          // Räkna severity från batches
          let criticalCount = 0;
          let highCount = 0;
          let mediumCount = 0;
          let lowCount = 0;

          revisionBatches.forEach(batch => {
            const result = batch.result_json as any;
            if (result?.findings) {
              result.findings.forEach((finding: any) => {
                if (finding.severity === 'critical') criticalCount++;
                else if (finding.severity === 'high') highCount++;
                else if (finding.severity === 'medium') mediumCount++;
                else if (finding.severity === 'low') lowCount++;
              });
            }
          });

          return {
            revisionId: revision.id,
            fileName: revision.file_name,
            createdAt: revision.created_at,
            completedAt: revision.completed_at,
            status: revision.status,
            flagCount: aggregate?.flag_count || 0,
            highRiskFlags: aggregate?.high_risk_flags || 0,
            riskScore: aggregate?.risk_score || 0,
            criticalCount,
            highCount,
            mediumCount,
            lowCount,
          };
        });

        setHistory(historyItems);
      } catch (err) {
        console.error('[useAnalysisHistory] Error:', err);
        setError(err instanceof Error ? err.message : 'Okänt fel vid hämtning av historik');
      } finally {
        setIsLoading(false);
      }
    }

    if (auditId && moduleType) {
      fetchHistory();
    }
  }, [auditId, moduleType]);

  return { history, isLoading, error };
}
